import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import TerminalWindow from '../TerminalWindow';
import { Check, Clear, Refresh } from '@mui/icons-material';
import AlertDialog from '../AlertDialog';
import { buildPipelineMessage, callAPIAsync, callAPISync, getMessages, postInsertDuplicates } from '../../utils/api';
import Logger from '../../utils/logger';
import {
	cancelProcessing,
	finishProcessing,
	getDuplicates,
	getProcessingComplete,
	incrementDuplicates,
} from '../../store/processing/processingSlice';
import { ProcessingStatus } from '../../types/generic';
import { useLogto } from '@logto/react';
import { AppDispatch, RootState } from '../../store';
const REFRESH_RATE_S = 10;

const ProcessingView: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const { fileName, fileId, status, notifyOnComplete, duplicatePreference, duplicates, duplicateBatches, enrichments } = useAppSelector(
		(state) => state.processing
	);

	const processingComplete = useAppSelector((state) => getProcessingComplete(state));

	const [showAlert, setShowAlert] = useState(false);
	const [processingMessages, setProcessingMessages] = useState<string[]>([buildPipelineMessage('Request submitted', 'INFO')]);
	const [askDialogOpen, setAskDialogOpen] = useState(false);
	const testTimer = useRef<undefined | NodeJS.Timeout>(undefined);

	const startTimer = () => {
		testTimer.current = setInterval(() => {
			fetchMessages();
		}, REFRESH_RATE_S * 1000);
	};

	const stopTimer = () => {
		clearInterval(testTimer.current);
		testTimer.current = undefined;
	};

	const forceUpdate = () => {
		clearInterval(testTimer.current);
		fetchMessages();
		startTimer();
	};

	const processDuplicates = (insert: boolean) => {
		if (insert) {
			callAPISync(logto, (token) => {
				postInsertDuplicates(token, duplicateBatches as string[]).then(async (resp) => {
					if (resp.status === 200) {
						setProcessingMessages([
							...processingMessages,
							buildPipelineMessage(`${duplicates} duplicates reinserted successfully`, 'INFO'),
						]);
					} else {
						setProcessingMessages([
							...processingMessages,
							buildPipelineMessage(`Attempt to reinsert duplicates failed: ${await resp.json()}`, 'WARN'),
						]);
					}
				});
			});
		}
		dispatch(finishProcessing(true));
		setAskDialogOpen(false);
	};

	const fetchMessages = () => {
		callAPIAsync(logto, (token) =>
			getMessages('status.upload', fileId, token)
				.then(async (resp) => {
					if (resp.status === 200) {
						const newMessages = (await resp.json()) as string[];
						if (newMessages && newMessages.length > 0) {
							if (newMessages.some((msg) => msg.includes('SUCCESS'))) {
								stopTimer();
								const latestDuplicateValue = dispatch((_dispatch: AppDispatch, getState: () => RootState): number => {
									const state = getState();
									return getDuplicates(state);
								});
								if (duplicatePreference === 'ASK' && latestDuplicateValue > 0) {
									setAskDialogOpen(true);
								} else {
									dispatch(finishProcessing(true));
								}
							} else if (newMessages.some((val) => val.includes('FATAL'))) {
								stopTimer();
								dispatch(finishProcessing(false));
							}

							let newVals = [];

							// looking for duplicates
							if (newMessages.some((val) => val.includes('duplicates were found when trying to insert'))) {
								// tries to parse number of duplicates assuming it's the 6th entry when split by spaces
								// e.g. 2026-08-26 14:30:00Z | INFO | 100 duplicates were found when trying to insert
								const numDuplicates = parseInt(
									newMessages.filter((val) => val.includes('duplicates were found when trying to insert'))[0].split(' ')[5]
								);
								let extraText = '';
								if (!Number.isNaN(numDuplicates)) {
									dispatch(incrementDuplicates({ duplicates: numDuplicates, newBatch: fileId }));
									if (duplicatePreference === 'DISCARD') {
										extraText = ', discarding';
									} else if (duplicatePreference === 'INSERT') {
										extraText = ', inserting anyway';
									}
								} else {
									extraText = ', failed to find number of duplicates';
								}
								newVals = newMessages.map((v) => (v.includes('duplicates were found when trying to insert') ? v.concat(extraText) : v));
							} else {
								newVals = newMessages;
							}
							setProcessingMessages((prev) =>
								[...prev, ...newVals].sort((a, b) => {
									//only sort by first section (datetime)
									return a.split('|')[0].localeCompare(b.split('|')[0]);
								})
							);
						}
					} else if (!resp.ok) {
						throw new Error(resp.statusText);
					}
				})
				.catch((e) => {
					Logger.error(`Error fetching messages from API: ${String(e)}`);
					setProcessingMessages((prev) => [...prev, buildPipelineMessage(String(e), 'ERROR')]);
				})
		);
	};

	useEffect(() => {
		if (!processingComplete) {
			startTimer();
			return () => clearInterval(testTimer.current);
		} else if (testTimer.current) {
			//clean up running interval after processing finished
			const timerRef = testTimer.current;
			testTimer.current = undefined;
			return () => clearInterval(timerRef);
		}
	}, []);

	let terminalTitle = '';
	let terminalAdornment = <></>;
	switch (status) {
		case ProcessingStatus.IN_PROGRESS:
			terminalTitle = `Updating every ${REFRESH_RATE_S} seconds...`;
			terminalAdornment = (
				<Tooltip title="Refresh">
					<IconButton data-testid="force-refresh-button" onClick={() => forceUpdate()}>
						<Refresh fontSize="small" />
					</IconButton>
				</Tooltip>
			);
			break;
		case ProcessingStatus.ERRORED:
			terminalTitle = 'Processing Errored';
			terminalAdornment = <Clear data-testid="processing-error-icon" aria-label="Errored" />;
			break;
		case ProcessingStatus.FINISHED:
			terminalTitle = 'Processing Complete';
			terminalAdornment = <Check data-testid="processing-success-icon" aria-label="Succeeded" />;
			break;
	}

	return (
		<>
			<Stack spacing={2} width="100%" data-testid="processing-view">
				<Stack direction="row">
					<Typography variant="h4" component={'h2'}>
						Processing {fileName}
					</Typography>
				</Stack>

				{notifyOnComplete && (
					<Alert severity="info">
						{notifyOnComplete
							? `An email will be sent to you and any users on behalf of you when processing is complete, this tab may be closed and processing
						will continue in the background`
							: `Please stay on the page while this is ongoing. Updates can be viewed below`}
					</Alert>
				)}

				<Collapse in={processingComplete && enrichments.length > 0}>
					<Alert severity="success">
						{`${fileName} has finished processing, but the following enrichments will continue processing: `}
						{enrichments.join(', ')}
					</Alert>
				</Collapse>

				<TerminalWindow title={terminalTitle} content={processingMessages} titleAdornment={terminalAdornment} />

				<Button
					data-testid="cancel-processing-button"
					sx={{ width: '30%', alignSelf: 'left' }}
					variant={processingComplete ? 'contained' : 'outlined'}
					size="large"
					onClick={() => {
						if (processingComplete) {
							dispatch(cancelProcessing());
						} else {
							setShowAlert(true);
						}
					}}
				>
					{processingComplete ? 'Submit another file' : 'Cancel'}
				</Button>
			</Stack>

			<AlertDialog
				title="Cancel Processing?"
				message={[
					'Are you sure?',
					'Processing will continue in the background, but you will not be able to view updates',
					'Created dashboards will still be viewable through Kibana',
				]}
				isOpen={showAlert}
				leftButtonMessage="Yes"
				leftButtonAction={() => {
					dispatch(cancelProcessing());
					setShowAlert(false);
				}}
				rightButtonMessage="No"
				rightButtonAction={() => setShowAlert(false)}
			/>
			<AlertDialog
				keepOpen
				title="Duplicate entries found"
				message={[
					`${duplicates} duplicate entries were found when trying to insert into Elasticsearch`,
					'Would you like to insert these entries anyway?',
				]}
				isOpen={askDialogOpen}
				leftButtonMessage="Yes"
				leftButtonAction={() => processDuplicates(true)}
				rightButtonMessage="No"
				rightButtonAction={() => processDuplicates(false)}
			/>
		</>
	);
};

export default ProcessingView;
